/**
 * Die Sitzung erneuern — ein Weg für die ganze App.
 *
 * core stellt das Access-Token für 15 Minuten aus, das Cookie
 * `__elbklar_access` lebt genauso lange. Danach schickt der Browser es nicht
 * mehr mit, und jede Anfrage an die pm-api endet in 401, obwohl das
 * Refresh-Cookie (`__elbklar_refresh`) noch gilt. `POST /session/refresh` bei
 * core-api tauscht es gegen ein neues Paar.
 *
 * Bis zum 27.10.2026 schickte der API-Client bei jeder 401 sofort zur
 * Anmeldung (Seitenwechsel, ungespeicherte Eingaben weg); erneuert wurde nur
 * im 60-Sekunden-Takt der Sitzungsprüfung. Die Glocke fragt alle 30 Sekunden
 * und traf die Lücke nach jedem Ablauf mit hoher Wahrscheinlichkeit.
 *
 * EIN laufender Refresh für alle: Gleichzeitige 401er (Glocke, Listen,
 * Speichern, Sitzungsprüfung) warten auf denselben Aufruf. core rotiert das
 * Refresh-Token bei jedem Refresh; ein zweiter Refresh mit dem schon
 * verbrauchten Cookie ist nur im Gnadenfenster von 30 Sekunden harmlos
 * (`elbklar_core.auth.refresh.GNADENFENSTER_SEKUNDEN`), danach beendet er die
 * Sitzung in allen Tabs.
 */

// Auth service URL - centralized authentication
export const AUTH_SERVICE_URL = import.meta.env.VITE_AUTH_SERVICE_URL || 'https://api.elbklar.com';

/**
 * `erneuert`: core hat neue Cookies gesetzt.
 * `abgelehnt`: core sagt nein (401/403), die Sitzung ist vorbei.
 * `nicht_erreichbar`: Netz weg oder Server gestört. Die Sitzung kann noch
 * gelten, die Anmeldeseite hilft hier nicht.
 */
export type Erneuerung = 'erneuert' | 'abgelehnt' | 'nicht_erreichbar';

let laufend: Promise<Erneuerung> | null = null;
let stand = 0;

/**
 * Zählt die erfolgreichen Erneuerungen. Der API-Client merkt sich den Stand
 * beim Absenden: Kommt die 401 erst an, nachdem inzwischen erneuert wurde,
 * genügt ein zweiter Versuch ohne neuen Refresh.
 */
export function sitzungsStand(): number {
  return stand;
}

/** Sitzung erneuern; läuft schon eine Erneuerung, auf dieselbe warten. */
export function sitzungErneuern(): Promise<Erneuerung> {
  if (!laufend) {
    // `.finally` läuft erst nach der Zuweisung, auch wenn `fetch` sofort scheitert.
    laufend = erneuern().finally(() => {
      laufend = null;
    });
  }
  return laufend;
}

async function erneuern(): Promise<Erneuerung> {
  try {
    const antwort = await fetch(`${AUTH_SERVICE_URL}/session/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (antwort.ok) {
      stand += 1;
      return 'erneuert';
    }
    return antwort.status === 401 || antwort.status === 403 ? 'abgelehnt' : 'nicht_erreichbar';
  } catch {
    return 'nicht_erreichbar';
  }
}

/**
 * Zur Anmeldung, mit der aktuellen Seite als Rücksprung (`?ziel=`, Muster aus
 * account-web). Auf der Anmeldeseite selbst nichts tun.
 */
export function zurAnmeldung(): void {
  const { pathname, search } = window.location;
  if (pathname.startsWith('/login')) return;
  window.location.href = `/login?ziel=${encodeURIComponent(pathname + search)}`;
}

/**
 * Das `?ziel=` der Anmeldeseite, geprüft: nur ein Pfad innerhalb dieser App.
 * `//host` und `/\host` wären für den Browser ein anderer Host, ebenso
 * `/<Tab>/host`: Der Browser entfernt Tab und Zeilenumbruch beim Parsen.
 * Darum wie `sichererPfad` im Design System: kein Backslash, keine Steuerzeichen.
 */
export function rueckweg(ziel: string | null, ersatz = '/dashboard'): string {
  // eslint-disable-next-line no-control-regex
  if (!ziel || !/^\/(?![/\\])/.test(ziel) || /[\\\u0000-\u001f\u007f]/.test(ziel) || ziel.startsWith('/login')) {
    return ersatz;
  }
  return ziel;
}
